import React, { FC, useEffect } from 'react';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import styles from './HelpSection.module.scss';
import { ITheme } from '@/src/lib/types/themeInterface';
import helpSectionImg from '@/public/images/themes/helpSection/help-section.webp';
import NextImage from '@/src/components/ui/nextImage/NextImage';
import LiveChatIcon from './liveChatIcon/LiveChatIcon';
import DocumentationIcon from './documentationIcon/DocumentationIcon';
import TicketIcon from './ticketIcon/TicketIcon';
import { baseUrl } from '@/src/lib/utils/baseUrl';
import classNames from 'classnames';

interface IHelpSection {
  themeSlug?: ITheme['slug'];
  documentationLink?: string;
  backgroundColor?: 'grey' | 'white';
}

const HelpSection: FC<IHelpSection> = ({
  themeSlug,
  documentationLink,
  backgroundColor = 'grey',
}) => {
  const ticketFormURL = process.env.NEXT_PUBLIC_ZENDESK_FORM_URL || baseUrl('help-center');
  const documentationURL = documentationLink || baseUrl(`help-center/documentation/${themeSlug}`);

  useEffect(() => {
    const launcherChat = document.getElementById('launchChat');
    if (!launcherChat) return;

    const handleClick = (e: Event) => {
      e.preventDefault();
      (window as any).zE?.('messenger', 'open');
    };

    launcherChat.addEventListener('click', handleClick);
    (window as any).zE?.('messenger:on', 'close');

    return () => {
      launcherChat.removeEventListener('click', handleClick);
    };
  }, []);

  return (
    <section
      className={classNames(
        styles.helpSection,
        styles[`helpSection--${backgroundColor}`],
        !themeSlug && styles['helpSection--noTheme']
      )}>
      <Wrapper>
        <div className={styles.helpSection__block}>
          <div className={styles.helpSection__about}>
            <h2 className={styles.helpSection__title}>One-of-a-Kind Support You Can Rely On</h2>
            <p className={styles.helpSection__text}>
              Our team is here to help on business days, with typical response times of 15 minutes
              during support hours, even during your trial. Choose the option that works best for
              you.
            </p>
          </div>
          <div className={styles.helpSection__image}>
            <NextImage
              src={helpSectionImg.src}
              alt="Support chat with 15 minutes response time"
              fill
              loading="lazy"
              sizes="(max-width: 1480px) 540px, (max-width: 1360px) 500px, (max-width: 1200px) 450px, (max-width: 1024px) calc(100vw - 200px), (max-width: 650px) calc(100vw - 160px), (max-width: 576px) calc(100vw - 48px), 624px"
            />
          </div>

          <div className={styles.helpSection__actions}>
            <div className={styles.helpSection__column}>
              <div className={styles.helpSection__item}>
                <div className={styles.helpSection__head}>
                  <LiveChatIcon />
                  <h3 className={styles.helpSection__label}>
                    Live Chat
                    <span>
                      Online <em>•</em>
                    </span>
                  </h3>
                  <p className={styles.helpSection__desc}>Real-time help from our Support team</p>
                </div>
                <div className={styles.helpSection__button}>
                  <a href="#" id="launchChat" className={styles.helpSection__start}>
                    Start a Live Chat
                  </a>
                </div>
              </div>
            </div>

            <div className={styles.helpSection__column}>
              <div className={styles.helpSection__item}>
                <div className={styles.helpSection__head}>
                  <TicketIcon />
                  <h3 className={styles.helpSection__label}>Submit Ticket</h3>
                  <p className={styles.helpSection__desc}>Request a Fix or Customization</p>
                </div>
                <div className={styles.helpSection__button}>
                  <a href={ticketFormURL} target="_blank" rel="noopener noreferrer">
                    Send a Request
                  </a>
                </div>
              </div>
            </div>

            {themeSlug && (
              <div className={styles.helpSection__column}>
                <div className={styles.helpSection__item}>
                  <div className={styles.helpSection__head}>
                    <DocumentationIcon />
                    <h3 className={styles.helpSection__label}>Documentation</h3>
                    <p className={styles.helpSection__desc}>
                      How-to guidance for theme setup and use
                    </p>
                  </div>
                  <div className={styles.helpSection__button}>
                    {documentationURL && (
                      <a href={documentationURL} target="_blank" rel="noopener noreferrer">
                        View Documentation
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </Wrapper>
    </section>
  );
};

export default HelpSection;
